import type { Action } from '@deskworks/shared-types';
import { useState } from 'react';
import { JsonBlock } from '@/components/code-block';
import { Markdown } from '@/components/markdown';
import { intlLocale, t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** Longer text than this (or with line breaks) is shown as a preview that opens on click. */
const SHORT_TEXT = 160;

/** `meta_title` / `metaTitle` → "Meta title". */
export function argumentLabel(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

/** HTML from a tool's argument (an article body, an email) as plain text with its paragraphs – it is never rendered. */
export function htmlText(html: string): string {
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote|section|article)>/gi, '\n\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const looksLikeHtml = (text: string) => /<\/?(p|div|h[1-6]|br|ul|ol|li|a|strong|em|span|section|article|img)\b[^>]*>/i.test(text);
const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isEmpty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length) || (isPlainObject(v) && !Object.keys(v).length);

/**
 * What a tool call will do, in a form a person can check before approving: one row per argument, long text (an
 * article, an email body) as a readable preview, nested values as rows of their own.
 */
export function ActionArguments({ args, info = {}, depth = 0 }: { args: Record<string, unknown>; info?: Action['argumentInfo']; depth?: number }) {
  const entries = Object.entries(args).filter(([, v]) => !isEmpty(v));
  if (!entries.length) return <p className="text-xs text-muted-foreground">{t('No arguments.')}</p>;
  return (
    <dl className={cn('grid gap-x-3 gap-y-1.5 text-sm sm:grid-cols-[minmax(6rem,auto)_1fr]', depth > 0 && 'border-l pl-3')}>
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="pt-px text-xs font-medium text-muted-foreground" title={info[key]?.description}>
            {info[key]?.title ?? argumentLabel(key)}
          </dt>
          <dd className="min-w-0 break-words">
            <ArgumentValue value={value} depth={depth} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ArgumentValue({ value, depth }: { value: unknown; depth: number }) {
  if (typeof value === 'string') return <TextValue text={value} />;
  if (typeof value === 'boolean') return <>{value ? t('Yes') : t('No')}</>;
  if (typeof value === 'number') return <>{value.toLocaleString(intlLocale)}</>;
  if (Array.isArray(value) && value.every((v) => ['string', 'number', 'boolean'].includes(typeof v)) && value.join(', ').length <= SHORT_TEXT * 2) {
    return <>{value.join(', ')}</>;
  }
  if (isPlainObject(value) && depth < 2) return <ActionArguments args={value} depth={depth + 1} />;
  if (Array.isArray(value) && depth < 2 && value.every(isPlainObject)) {
    return (
      <ol className="flex flex-col gap-2">
        {value.map((v, i) => (
          <li key={i}>
            <ActionArguments args={v} depth={depth + 1} />
          </li>
        ))}
      </ol>
    );
  }
  return <JsonBlock value={value} maxHeight="max-h-48" />;
}

function TextValue({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const html = looksLikeHtml(text);
  const shown = html ? htmlText(text) : text;
  if (shown.length <= SHORT_TEXT && !shown.includes('\n')) return <span className="whitespace-pre-wrap">{shown}</span>;
  const words = shown.split(/\s+/).filter(Boolean).length;
  return (
    <div className="rounded-md border bg-muted/30">
      <div className={cn('relative overflow-hidden px-3 py-2', !open && 'max-h-40')}>
        <Markdown text={shown} className="text-[13px]" />
        {!open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />}
      </div>
      <button
        type="button"
        className="w-full cursor-pointer border-t px-3 py-1 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        {open
          ? t('Show less')
          : html
            ? t('Show all · {n} words (HTML, shown as text)', { n: words.toLocaleString(intlLocale) })
            : t('Show all · {n} words', { n: words.toLocaleString(intlLocale) })}
      </button>
    </div>
  );
}
